import { createFileRoute, Link } from '@tanstack/react-router';
import { ExternalUrls } from '../../lib/constants';
import { useSEO, useBreadcrumbSchema, PageSEO } from '../../lib/seo';
import { Box, ButtonBase, Container, Grid, Typography, Button, Stack, useMediaQuery } from '@mui/material';
import { useState, useEffect, useRef } from 'react';
import PsychologyAltIcon from '@mui/icons-material/PsychologyAlt';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import Diversity1Icon from '@mui/icons-material/Diversity1';
import AlarmIcon from '@mui/icons-material/Alarm';
import InsightsIcon from '@mui/icons-material/Insights';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import FlagIcon from '@mui/icons-material/Flag';
import Diversity3Icon from '@mui/icons-material/Diversity3';
import ForumIcon from '@mui/icons-material/Forum';

import { Section } from '../../components/ui/Section';
import { SectionHeader } from '../../components/ui/SectionHeader';
import { Hero } from '../../components/ui/Hero';
import { CareCircle } from '../../components/ui/CareCircle';
import { CallToActionCard } from '../../components/ui/CallToActionCard';
import { MetricsSection } from '../../components/sections/MetricsSection';
import { BrandBackdrop } from '../../components/brand/BrandBackdrop';
import { FadeIn } from '../../components/ui/FadeIn';
import { InfoCard } from '../../components/ui/InfoCard';
import { FeatureIcon } from '../../components/ui/FeatureIcon';
import { PhoneNotification } from '../../components/ui/PhoneNotification';
import { NeutralColors, BrandColors } from '@neumind/shared/branding';

export const Route = createFileRoute('/solutions/clients')({
  component: ClientsPage,
});

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`toolkit-tabpanel-${index}`}
      aria-labelledby={`toolkit-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const featureCards = [
  {
    icon: '/images/clients/reminder.svg',
    title: 'Remember what matters',
    description: 'Scheduled Reminders make meds, appointments and home exercises hard to miss.',
  },
  {
    icon: '/images/clients/tracker.svg',
    title: 'See and feel progress',
    description: 'Trackers & Journals help you log mood, fatigue or practice time and spot patterns to celebrate wins.',
  },
  {
    icon: '/images/clients/nudge.svg',
    title: "Help when it's hard to start",
    description: 'Daily Nudges arrive at helpful moments to spark action and build habits.',
  },
  {
    icon: '/images/clients/resource.svg',
    title: 'Everything in one place',
    description: 'Therapy handouts, links and videos live in Resources—no lost papers.',
  },
  {
    icon: '/images/clients/community.svg',
    title: 'Community that understands',
    description: 'Opt‑in Community provides peer tips and encouragement when you need it.',
  },
  {
    icon: '/images/clients/family.svg',
    title: 'Family included - when you need',
    description: 'Invite trusted people to your Care Circle so everyone sees the plan and can help with routines.',
  },
];

const journeySteps = [
  {
    title: 'A long road begins',
    description:
      "After a serious brain injury, Luke spent months in hospital and specialist rehab. The future was uncertain; progress came one small step at a time.",
    image: '/images/clients/journey-1.webp',
    imageAlt: 'Young man in a wheelchair in hospital',
    header: 'A long road begins...',
  },
  {
    title: 'The care cliff',
    description:
      "The real challenge started after discharge—limited contact, uncertainty, more to remember, and no easy way to carry therapy into daily life.",
    image: '/images/clients/journey-2.webp',
    imageAlt: 'Desk with papers and notes',
    header: 'Hitting the care cliff...',
  },
  {
    title: 'Strategies that Stick',
    description:
      "In specialist neurorehab, Luke learned practical strategies—written cues, step-by-step tasks, and a paging device—ways to remember and start things at home.",
    image: '/images/clients/journey-3.webp',
    imageAlt: 'Smartphone with neumind app',
    header: 'Finding strategies that stick...',
  },
  {
    title: 'Surviving to Thriving',
    description:
      'What began with Luke became a platform shaped by the ABI community: routines, reminders, trackers and a Care Circle to coordinate support.',
    image: '/images/clients/journey-4v2.webp',
    imageAlt: 'Two young men smiling outdoors',
    header: 'Surviving to thriving...',
  },
];

const featureTabs = [
  {
    label: 'Routines & Reminders',
    icon: AlarmIcon,
    tagline: 'Tackling life one prompt at a time',
    title: 'Turn plans into daily steps and stay organised',
    description:
      "Schedule prompts for tasks and meds, and get an alert when it's time. This takes the pressure off your brain so you can focus on the more interesting stuff. Create simple routines (e.g., \"Morning memory warm-up\" or \"Balance exercises\") and build consistent habits.",
    image: '/images/product/sarah/app-reminder-alert-390.webp',
    imageAlt: 'Mobile app reminder alert asking how your energy is right now',
    color: BrandColors.error.light,
  },
  {
    label: 'Trackers & Journals',
    icon: InsightsIcon,
    tagline: 'Bring the best out of you',
    title: 'See and feel progress',
    description:
      'Log mood, fatigue, headaches or practice time with quick sliders and jot down daily notes. The app turns these into simple graphs over time so you and your therapist can spot patterns and changes.',
    image: '/images/product/sarah/app-fatigue-tracker-390.webp',
    imageAlt: 'Mobile app fatigue tracker with activity stats and trend chart',
    color: BrandColors.warning.light,

  },
  {
    label: 'Nudges & Strategies',
    icon: TipsAndUpdatesIcon,
    tagline: 'The right nudge at the right moment',
    title: 'Gentle nudges to remember and practise',
    description:
      "Can't remember your favourite joke or keep forgetting that strategy your therapist taught you? Not a problem. These smart nudges give you a little push to get started when motivation is low.",
    // VER-260729 walk: the old nudges.png screenshot was dated — replaced
    // with coded phone-OS-style notifications (same component as the
    // homepage hero), rendered in useNudges below.
    image: null,
    imageAlt: '',
    useNudges: true,
    color: BrandColors.info.light,
  },
  {
    label: 'Goals & Resources',
    icon: FlagIcon,
    tagline: 'The important stuff all in one place',
    title: 'Your journey, your story',
    description:
      'Work on goals set with your therapist and break them into simple tasks to make progress simple and keep up momentum. Keep all your therapy resources (like worksheets, tips or videos) in one place.',
    image: '/images/product/sarah/app-home-390.webp',
    imageAlt: 'Mobile app home screen showing rehab goals and daily plan',
    color: BrandColors.success.light,
  },
  {
    label: 'Care Circle',
    icon: Diversity3Icon,
    tagline: 'Everyone on the same page',
    title: 'Invite your Care Circle',
    description:
      'Invite trusted family members or clinicians into your Care Circle. They can (with your consent) view your tasks or entries and help you stay on track so everyone works together as a team.',
    image: null,
    imageAlt: '',
    useCareCircle: true,
    color: BrandColors.secondary.main,
  },
  {
    label: 'Community',
    icon: ForumIcon,
    tagline: "You don't need to walk alone",
    title: 'A community that understands',
    description:
      "Join a supportive community of neumind users to share stories, tips, and encouragement. Connect with others who have similar experiences, so you don't feel alone on your journey.",
    image: '/images/clients/Community.png',
    imageAlt: 'Mobile app showing community forum',
    color: BrandColors.primary.light,
  },
];

/**
 * Phone-OS-style nudge examples for the "Nudges & Strategies" tab
 * (VER-260729 walk: the previous nudges.png asset was an old, near-empty
 * screenshot). Same PhoneNotification component as the homepage hero.
 */
function NudgeShowcase() {
  const nudges = [
    {
      icon: <TipsAndUpdatesIcon />,
      title: 'A nudge from your plan',
      body: 'Pacing check: take a short rest before the next task.',
      time: 'now',
      accentColor: BrandColors.info.main,
    },
    {
      icon: <PsychologyAltIcon />,
      title: 'Strategy practice',
      body: 'Try the memory routine you set with your OT.',
      time: '2 h ago',
      accentColor: BrandColors.primary.main,
    },
    {
      icon: <MenuBookIcon />,
      title: 'Small step',
      body: 'Open your planner and pick one thing for today.',
      time: 'Yesterday',
      accentColor: BrandColors.success.main,
    },
  ];
  return (
    <Box
      role="img"
      aria-label="Examples of neumind nudges: a pacing check, a strategy practice prompt and a small first step"
      sx={{ width: '100%', maxWidth: 380, mx: 'auto' }}
    >
      <Stack spacing={2} aria-hidden>
        {nudges.map((n, index) => (
          <FadeIn key={n.title} delay={index * 150} direction="up" distance={16}>
            <PhoneNotification
              icon={n.icon}
              title={n.title}
              body={n.body}
              time={n.time}
              accentColor={n.accentColor}
            />
          </FadeIn>
        ))}
      </Stack>
    </Box>
  );
}

function ClientsPage() {
  useSEO(PageSEO.solutionsClients);
  useBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Solutions', url: '/solutions' },
    { name: 'For Clients', url: '/solutions/clients' }
  ]);

  const [activeTab, setActiveTab] = useState(0);
  const [activeJourneyStep, setActiveJourneyStep] = useState(0);

  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  const journeyRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Scroll-based intersection observer for journey steps
  useEffect(() => {
    const observers: IntersectionObserver[] = [];

    journeyRefs.current.forEach((ref, index) => {
      if (!ref) return;

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
              setActiveJourneyStep(index);
            }
          });
        },
        {
          threshold: 0.5,
          rootMargin: '-20% 0px -20% 0px',
        }
      );

      observer.observe(ref);
      observers.push(observer);
    });

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, []);

  return (
    <>
      {/* Hero Section */}
      <Hero
        maxWidth={false}
        showScrollIndicator={true}
        centered={false}
        sx={{
          background: `linear-gradient(135deg, ${NeutralColors[50]} 0%, ${NeutralColors[200]} 100%)`,
        }}
      >
        {/* Brand-atmosphere layer: warm, calm light mesh wash + a soft Care Circle
            constellation fading from the top-right shoulder. Kept subtler than the
            professionals tone for this survivor/carer audience — blue ≤10%, cyan ≤6%. */}
        <BrandBackdrop
          mesh="light"
          constellation
          constellationCorner="top-right"
          constellationOpacity={0.45}
        />
        <Box sx={{ maxWidth: 1200, mx: 'auto', px: 3, position: 'relative', zIndex: 1 }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <FadeIn direction="up" duration={800}>
                <Typography variant="h1" sx={{ mb: 3, textAlign: { xs: 'center', md: 'left' } }}>
                  Supporting you throughout{' '}
                  <Box component="span" sx={{ color: 'primary.main' }}>
                    your journey
                  </Box>
                </Typography>
              </FadeIn>
              <FadeIn direction="up" delay={150} duration={800}>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 4, fontSize: '1.125rem', textAlign: { xs: 'center', md: 'left' } }}
                >
                  Therapy plans turned into simple daily prompts and check‑ins you can actually follow.
                  Your family and care team stay in sync so they can support you on your terms.
                </Typography>
              </FadeIn>
              <FadeIn direction="up" delay={300} duration={800}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  sx={{ justifyContent: { xs: 'center', md: 'flex-start' } }}
                >
                  <Button variant="contained" size="large" component={Link} to="/contact-us">
                    Ask your clinician for an invite
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    component="a"
                    href={ExternalUrls.app}
                    target="_blank"
                  >
                    Download app
                  </Button>
                </Stack>
              </FadeIn>
            </Grid>
            <Grid item xs={12} md={6}>
              <FadeIn direction="left" delay={200} duration={800}>
                {/* Frame the real phone visual forward: a soft orb-halo glows
                    behind it for gentle navy-tinted elevation — calm and warm,
                    no invented product card. Dialled below the homepage opacity
                    to keep the survivor/carer tone soft. */}
                <Box sx={{ position: 'relative' }}>
                  <BrandBackdrop
                    motif="orb-halo"
                    motifCorner="top-right"
                    motifColor="37,99,235"
                    motifOpacity={0.12}
                    motifSize={460}
                  />
                  <Box
                    component="img"
                    src="/images/clients/holding-phone-1.png"
                    alt="Hand holding smartphone"
                    loading="eager"
                    decoding="async"
                    fetchPriority="high"
                    sizes="(max-width: 900px) 100vw, 500px"
                    sx={{
                      position: 'relative',
                      zIndex: 1,
                      width: '100%',
                      maxWidth: 500,
                      height: 'auto',
                      display: 'block',
                      mx: 'auto',
                    }}
                  />
                </Box>
              </FadeIn>
            </Grid>
          </Grid>
        </Box>
      </Hero>

      {/* Dark "You're not alone" Section */}
      <Section variant="dark" py={12}>
        <SectionHeader
          overline="You're not alone"
          title="The everyday can feel harder than it should"
          subtitle="After a condition like brain injury or stroke, remembering things, getting started and staying organised can feel overwhelming."
          centered
          variant="dark"
          sx={{ maxWidth: 700, mx: 'auto', mb: 8 }}
        />

        <Grid container spacing={4}>
          {[
            {
              icon: <PsychologyAltIcon sx={{ fontSize: 32 }} />,
              title: 'Life is complicated',
              description:
                'Tasks that used to be simple now take more effort. Remembering medication, appointments or home practice is hard without clear, timely prompts.',
              color: BrandColors.error.light,
            },
            {
              icon: <MenuBookIcon sx={{ fontSize: 32 }} />,
              title: "It's hard to know what to do",
              description:
                "You leave sessions with advice and handouts, then get home and wonder what to do first. Papers get lost and it's unclear how to use strategies in real life.",
              color: BrandColors.warning.light,
            },
            {
              icon: <Diversity1Icon sx={{ fontSize: 32 }} />,
              title: 'No one understands',
              description:
                'On the outside you may look "fine", so people expect you to be back to normal. They don\'t see the effort it takes to think, remember or manage energy.',
              color: BrandColors.info.light,
            },
          ].map((item) => (
            <Grid item xs={12} md={4} key={item.title}>
              <InfoCard
                variant="glass"
                title={item.title}
                description={item.description}
                icon={
                  <FeatureIcon
                    icon={item.icon}
                    color={item.color}
                  />
                }
              />
            </Grid>
          ))}
        </Grid>
      </Section>

      {/* How neumind helps Section */}
      <Section py={10} id="how-neumind-helps">
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <SectionHeader
              overline="How neumind helps"
              title="Bring therapy into everyday life"
              subtitle={
                <>
                  We built neumind with and for people whose brains work differently—after a brain
                  injury, stroke or dementia. Life is hard enough as it is, and you deserve tools
                  that make it easier. neumind turns therapy plans into{' '}
                  <strong>simple prompts, step‑by‑step tasks and useful guidance</strong>, so therapy
                  continues in everyday life. Your clinician can support your progress and adjust
                  the plan between visits. Family (if you choose) can be part of your Care Circle
                  to keep everyone in sync.
                </>
              }
              sx={{ mb: 2 }}
            />
            <Typography
              variant="body1"
              sx={{ fontStyle: 'italic', color: 'text.secondary' }}
            >
              Built by brain injury survivors and family members
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            {/* VER-260729 walk: refreshed from the dated for-clients-2.png
                composite to the current-generation app home screenshot. */}
            <Box
              component="img"
              src="/images/product/sarah/app-home-390.webp"
              alt="Mobile app home screen showing rehab goals and the day's schedule"
              sx={{
                width: '100%',
                maxWidth: 340,
                height: 'auto',
                display: 'block',
                mx: 'auto',
                borderRadius: 4,
                filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.15))',
              }}
            />
          </Grid>
        </Grid>

        {/* Feature Cards */}
        <Box sx={{ mt: 8 }}>
          <Grid container spacing={3}>
            {featureCards.map((card) => (
              <Grid item xs={12} sm={6} md={4} key={card.title}>
                <InfoCard
                  centered
                  title={card.title}
                  description={card.description}
                  icon={
                    <Box
                      component="img"
                      src={card.icon}
                      alt=""
                      sx={{ width: 48, height: 48 }}
                    />
                  }
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Section>

      {/* Twin Road to Recovery Journey Section */}
      <Section variant="gradient" py={10}>
        <Grid container spacing={6}>
          {/* Desktop: Sticky image on left */}
          <Grid item xs={12} md={5} sx={{ display: { xs: 'none', md: 'block' } }}>
            <Box
              sx={{
                position: 'sticky',
                top: 80,
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  borderRadius: 3,
                  overflow: 'hidden',
                  height: 580,
                }}
              >
                {/* Header overlay at top */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    p: 3,
                    zIndex: 2,
                    background: 'linear-gradient(rgba(0,0,0,0.65), transparent 80%)',
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      color: 'white',
                      fontWeight: 700,
                      textShadow: '0 2px 6px rgba(0,0,0,0.4)',
                    }}
                  >
                    {journeySteps[activeJourneyStep].header}
                  </Typography>
                </Box>
                {/* Images with crossfade */}
                {journeySteps.map((step, index) => (
                  <Box
                    key={step.title}
                    component="img"
                    src={step.image}
                    alt={step.imageAlt}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      opacity: activeJourneyStep === index ? 1 : 0,
                      transition: prefersReducedMotion ? 'none' : 'opacity 0.5s ease',
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Grid>

          {/* Content on right */}
          <Grid item xs={12} md={7}>
            {/* Intro section */}
            <Box sx={{ mb: 5 }}>
              <Typography variant="overline" color="primary" sx={{ mb: 1, display: 'block' }}>
                Born from personal experience
              </Typography>
              <Typography variant="h2" sx={{ mb: 2 }}>
                A Twin Road to Recovery
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Our story starts at home: Luke's brain injury exposed the gaps between
                sessions—remembering, starting, keeping momentum. Working with clinicians,
                survivors and caregivers, Luke and his identical twin Ellis turned those needs
                into practical, day-to-day tools. From the beginning, we've built neumind with
                and for the community, combining therapy with what helps in real life.
              </Typography>
              <Button variant="outlined" component={Link} to="/about" size="large">
                Luke's Journey →
              </Button>
            </Box>

            {/* Timeline items - each one tall enough to trigger scroll */}
            {journeySteps.map((step, index) => (
              <Box
                key={step.title}
                ref={(el: HTMLDivElement | null) => { journeyRefs.current[index] = el; }}
                sx={{
                  minHeight: { md: 320 },
                  py: 5,
                  opacity: activeJourneyStep === index ? 1 : 0.6,
                  transition: prefersReducedMotion ? 'none' : 'opacity 0.4s ease',
                }}
              >
                <Typography
                  variant="h4"
                  sx={{ mb: 2, fontWeight: 700 }}
                >
                  {step.title}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ fontSize: '1.2rem', lineHeight: 1.8 }}
                >
                  {step.description}
                </Typography>
                {/* Mobile image */}
                <Box
                  component="img"
                  src={step.image}
                  alt={step.imageAlt}
                  sx={{
                    display: { xs: 'block', md: 'none' },
                    width: '100%',
                    maxWidth: 500,
                    borderRadius: 2,
                    mt: 3,
                  }}
                />
              </Box>
            ))}
          </Grid>
        </Grid>
      </Section>

      {/* Your Path to Recovery - Tabbed Section */}
      <Section py={10}>
        <SectionHeader
          overline="The tools to live life your way"
          title="Everyday tools for your recovery"
          subtitle="neumind turns your plan into small, clear steps with simple goals and bite-sized guidance. Your care team can see your plan, so practice keeps going between sessions."
          centered
          sx={{ maxWidth: 700, mx: 'auto', mb: 6 }}
        />

        {/* Feature selector cards */}
        <Grid container spacing={2} sx={{ mb: 4 }} role="tablist" aria-label="Toolkit features">
          {featureTabs.map((tab, index) => {
            const IconComponent = tab.icon;
            const isActive = activeTab === index;
            return (
              <Grid item xs={6} sm={4} md={2} key={tab.label}>
                <ButtonBase
                  onClick={() => setActiveTab(index)}
                  role="tab"
                  id={`toolkit-tab-${index}`}
                  aria-selected={isActive}
                  aria-controls={`toolkit-tabpanel-${index}`}
                  sx={{
                    p: 2,
                    width: '100%',
                    height: '100%',
                    minHeight: 100,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 2,
                    textAlign: 'center',
                    transition: 'all 0.2s ease',
                    bgcolor: isActive ? 'primary.dark' : 'background.paper',
                    color: isActive ? 'primary.main' : 'text.primary',
                    boxShadow: isActive ? 4 : 1,
                    border: '2px solid',
                    borderColor: isActive ? 'primary.main' : 'transparent',
                    transform: isActive ? 'scale(1.02)' : 'scale(1)',
                    '&:hover': {
                      bgcolor: isActive ? 'primary.dark' : 'primary.50',
                      boxShadow: 3,
                      transform: 'scale(1.02)',
                      borderColor: 'primary.main',
                    },
                    '&:focus-visible': {
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: 2,
                    },
                  }}
                >

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      mb: 1.5,
                      bgcolor: isActive ? 'white' : 'transparent',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <IconComponent
                      sx={{
                        fontSize: 28,
                        color: isActive ? tab.color : 'text.secondary',
                        transition: 'color 0.2s ease',
                      }}
                    />
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      lineHeight: 1.3,
                      color: isActive ? 'white' : 'text.primary',
                    }}
                  >
                    {tab.label}
                  </Typography>
                </ButtonBase>
              </Grid>
            );
          })}
        </Grid>

        {
          featureTabs.map((tab, index) => (
            <TabPanel key={tab.label} value={activeTab} index={index}>
              <Box
                sx={{
                  bgcolor: 'background.paper',
                  borderRadius: 3,
                  boxShadow: 3,
                  overflow: 'hidden',
                }}
              >
                <Grid container>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ p: { xs: 3, md: 5 } }}>
                      <Typography
                        variant="overline"
                        color="primary"
                        sx={{ mb: 1, display: 'block' }}
                      >
                        {tab.tagline}
                      </Typography>
                      <Typography variant="h3" sx={{ mb: 3 }}>
                        {tab.title}
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        {tab.description}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        height: '100%',
                        minHeight: 400,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 3,
                      }}
                    >
                      {tab.useNudges ? (
                        <NudgeShowcase />
                      ) : tab.useCareCircle ? (
                        <CareCircle />
                      ) : (
                        <Box
                          component="img"
                          src={tab.image!}
                          alt={tab.imageAlt}
                          sx={{
                            maxWidth: '100%',
                            maxHeight: 450,
                            objectFit: 'contain',
                            filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.15))',
                            transform: 'scale(1)',
                            animation: prefersReducedMotion
                              ? 'none'
                              : 'fadeInScale 0.6s cubic-bezier(0.2, 0.8, 0.2, 1)',
                            '@keyframes fadeInScale': {
                              '0%': { opacity: 0, transform: 'scale(0.95) translateY(10px)' },
                              '100%': { opacity: 1, transform: 'scale(1) translateY(0)' },
                            },
                          }}
                        />
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </TabPanel>
          ))
        }
      </Section>

      {/* Stats Section — VER-260729 walk: a single metric read sparse;
          verified deliberate in code but widened to three so the section
          carries its weight (intro reworded to cover measured engagement,
          not only self-report). */}
      <MetricsSection
        variant="gradient"
        py={8}
        title="Turning therapy into daily action"
        intro="From real-world use of neumind:"
        metricIds={['independence', 'taskAdherence', 'promptsDelivered']}
        alignMetrics="center"
        metricValueColor="primary.main"
      />

      {/* CTA Section */}
      <Box sx={{ py: 12, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <CallToActionCard
            location="clients_bottom_cta"
            title="Start your recovery journey today"
            description="Take the first step towards personalised support and improved independence"
            primaryCTA={{ label: "Ask your clinician for an invite", href: "/contact-us" }}
            secondaryCTA={{ label: "Download app", href: ExternalUrls.app, external: true }}
          />
        </Container>
      </Box>
    </>
  );
}
